import { AutoAwesome, Edit, InfoOutlined, Refresh } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Grid, Tooltip } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";

import { isCopilotHumanEdited } from "../utils/copilotExtensions";

interface CopilotNoteActionProps {
  copilotData: any;
  populating: boolean;
  generating: boolean;
  readOnly: boolean;
  hasLinkedEncounter: boolean;
  onPopulateEncounter: () => void;
  onGenerateNote: () => void;
}

/** True when a vital measurement has a real value (not just a default unit). */
function hasVitalMeasurementValue(measurement: unknown): boolean {
  if (measurement == null || measurement === "") return false;
  if (typeof measurement === "number") return true;
  if (typeof measurement === "string") return measurement.trim().length > 0;
  if (typeof measurement !== "object") return false;

  const entry = measurement as Record<string, unknown>;

  // Blood pressure: { systolic: { value }, diastolic: { value } } or legacy numbers
  if ("systolic" in entry || "diastolic" in entry) {
    return (
      hasVitalMeasurementValue(entry.systolic) ||
      hasVitalMeasurementValue(entry.diastolic)
    );
  }

  const value = entry.value;
  if (value == null || value === "") return false;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
}

function hasMeaningfulVitals(vitals: unknown): boolean {
  if (!vitals || typeof vitals !== "object") return false;
  return Object.values(vitals as object).some(hasVitalMeasurementValue);
}

/** True when a nested note section has meaningful content (ignores empty shells). */
function hasMeaningfulObjectContent(value: unknown): boolean {
  if (value == null || value === "") return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number" || typeof value === "boolean") return true;
  if (Array.isArray(value)) {
    return value.some(hasMeaningfulObjectContent);
  }
  if (typeof value === "object") {
    return Object.values(value as object).some(hasMeaningfulObjectContent);
  }
  return false;
}

/** True when the note has been generated with at least one content field. */
function hasGeneratedNoteContent(note: any): boolean {
  if (!note || typeof note !== "object") return false;

  const contentKeys = [
    "chiefComplaint",
    "hpi",
    "vitals",
    "reviewOfSystems",
    "physicalAssessment",
    "history",
    "diagnoses",
    "plan",
  ] as const;

  return contentKeys.some((key) => {
    const value = note[key];
    if (value == null) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (key === "vitals") return hasMeaningfulVitals(value);
    if (typeof value === "object") return hasMeaningfulObjectContent(value);
    return false;
  });
}

const CopilotNoteAction = ({
  copilotData,
  populating,
  generating,
  readOnly,
  hasLinkedEncounter,
  onPopulateEncounter,
  onGenerateNote,
}: CopilotNoteActionProps) => {
  const { enableAiNoteAcceptance } = useFlags();
  const noteAlreadyGenerated = hasGeneratedNoteContent(copilotData?.note);
  const isNoteFinalized =
    copilotData?.status === "finalized" || copilotData?.status === "completed";
  const showReopenToGenerateTooltip =
    isNoteFinalized && !noteAlreadyGenerated && !hasLinkedEncounter;
  const canPopulate =
    noteAlreadyGenerated && hasLinkedEncounter && !readOnly && !generating;
  const isGenerateDisabled = readOnly || populating || noteAlreadyGenerated;
  const generateTooltipTitle = showReopenToGenerateTooltip
    ? "Reopen this note to generate a clinical note from your transcript."
    : "";
  const showAcceptAll =
    !!enableAiNoteAcceptance && !!copilotData && hasLinkedEncounter;

  const generateButton = (
    <LoadingButton
      fullWidth
      loading={generating}
      disabled={isGenerateDisabled}
      variant="contained"
      color="primary"
      startIcon={noteAlreadyGenerated ? <Refresh /> : <Edit />}
      onClick={onGenerateNote}
    >
      Generate Note
      {isCopilotHumanEdited(copilotData) && (
        <Tooltip title="Note has been edited and cannot be regenerated">
          <InfoOutlined sx={{ ml: 1 }} fontSize="small" />
        </Tooltip>
      )}
    </LoadingButton>
  );

  return (
    <Grid item container xs={12}>
      <Grid container spacing={2}>
        {showAcceptAll && (
          <Grid item xs={6}>
            <LoadingButton
              fullWidth
              loading={populating}
              disabled={!canPopulate || populating}
              startIcon={<AutoAwesome />}
              variant="outlined"
              color="primary"
              onClick={onPopulateEncounter}
            >
              Accept All
            </LoadingButton>
          </Grid>
        )}

        <Grid item xs={showAcceptAll ? 6 : 12}>
          {generateTooltipTitle ? (
            <Tooltip title={generateTooltipTitle}>
              <span>{generateButton}</span>
            </Tooltip>
          ) : (
            generateButton
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CopilotNoteAction;
